/**
 * The just-in-time Transfer Balance Cap question — shown only when the $1.6m screen trips for a
 * person (transferBalanceCap.ts `tbcQuestionNeededFor`): large pension balances AND a strategy
 * about to model commencing a new pension. Most files never see it.
 *
 * Answering records the ATO figure on the person (asked once, ever). Skipping lets the tool
 * estimate cap usage from current pension balances — the estimate and its limitation are stated in
 * the SOA assumptions. Either way the engine splits any commencement at the cap, so no strategy
 * can recommend more than the law allows.
 */
import React, { useState } from 'react';
import { Button, CurrencyInput } from './ui/index.tsx';
import { formatCurrency } from '../utils/index.ts';
import { GENERAL_TRANSFER_BALANCE_CAP } from '../constants/index.ts';

export interface TransferCapGateProps {
    /** The person's display name ("Rocky"). */
    name: string;
    onAnswer: (lifetimeAmount: number) => void;
    onSkip: () => void;
}

const TransferCapGate = ({ name, onAnswer, onSkip }: TransferCapGateProps) => {
    const [amount, setAmount] = useState<number | ''>('');
    return (
        <div className="mt-3 rounded-xl bg-white border border-slate-200/70 border-l-4 border-l-blue-500 p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">One more thing — {name}&apos;s pension cap</p>
            <p className="text-sm text-slate-700 mt-1.5">
                Super held in a retirement pension pays <strong>no tax at all</strong> on its earnings, so the government limits how
                much any one person may move into one over their lifetime — currently{' '}
                <strong>{formatCurrency(GENERAL_TRANSFER_BALANCE_CAP)}</strong>. That limit is called the{' '}
                <strong>transfer balance cap</strong>, and {name}&apos;s balances are large enough that it may now cap how much more can
                go in. Roughly how much has {name} moved into retirement-phase pensions over their lifetime? The ATO shows this in myGov
                as the <strong>&ldquo;transfer balance account&rdquo;</strong>.
            </p>
            <div className="mt-3 flex items-end gap-3 flex-wrap">
                <div className="w-56">
                    <CurrencyInput label="Lifetime amount moved into pensions ($)" value={amount} onChange={(v) => setAmount(v ?? '')} />
                </div>
                <Button size="sm" disabled={amount === ''} onClick={() => amount !== '' && onAnswer(Number(amount))}>
                    Save &amp; continue
                </Button>
                <Button variant="secondary" size="sm" onClick={onSkip}>
                    Not sure &mdash; estimate from current pension balances
                </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2.5">
                Saved to the client profile, so you&apos;ll only be asked once. If estimated, the assumption is stated in the
                Statement of Advice.
            </p>
        </div>
    );
};

export default TransferCapGate;
